import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { isDevRegComplete } from '@/lib/devregStorage';
import { isSwitchComplete } from '@/lib/switchStorage';
import { isSwitchReasonComplete } from '@/lib/switchReasonStorage';
import { isNpdAwarenessComplete, isNpdAwarenessCorrect } from '@/lib/npdAwarenessStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { Navigate, useNavigate } from 'react-router-dom';

export default function NpdResult({ variant }: { variant: 'correct' | 'wrong' }) {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isDevRegComplete()) {
    return <Navigate to="/devreg" replace />;
  }
  if (!isSwitchComplete()) {
    return <Navigate to="/switch" replace />;
  }
  if (!isSwitchReasonComplete()) {
    return <Navigate to="/switch-reason" replace />;
  }
  if (!isNpdAwarenessComplete()) {
    return <Navigate to="/npd-awareness" replace />;
  }
  if (variant === 'correct' && !isNpdAwarenessCorrect()) {
    return <Navigate to="/npd-wrong" replace />;
  }
  if (variant === 'wrong' && isNpdAwarenessCorrect()) {
    return <Navigate to="/npd-correct" replace />;
  }

  const title = variant === 'correct' ? t.npdResult.correctTitle : t.npdResult.wrongTitle;

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!bg-[url('/images/Magenta.jpeg')] !bg-cover !bg-center !px-5 !py-6">
              <p className="m-0 text-center text-[18px] font-bold leading-relaxed text-white/95 sm:text-[20px]">
                {title}
              </p>
              <p className="m-0 mt-5 whitespace-pre-line text-center text-[17px] font-normal leading-relaxed text-white/95 sm:text-[18px]">
                {variant === 'wrong' ? (
                  <>
                    {t.npdResult.wrongBodyBefore}
                    <span className="font-bold">{t.npdResult.wrongBodyHighlight}</span>
                    {t.npdResult.wrongBodyAfter}
                  </>
                ) : (
                  <>
                    {t.npdResult.bodyBefore}
                    <span className="font-bold">{t.npdResult.bodyHighlight}</span>
                    {t.npdResult.bodyAfter}
                  </>
                )}
              </p>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
          <SurveyBrownPanelInset className="mt-8">
            <div className="flex justify-center">
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={() => navigate('/tradein')}
              >
                {t.npdResult.continue}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
